// historyNavGating — the PURE caret-gating + menu-precedence decision for the
// composer's message-history recall (A-11, Arrow-Up/Down shell/readline style).
//
// Extracted out of PowerComposer's `onKeyDown` so the make-or-break logic (the
// `\n`-scan, the no-selection gate, the direction/menu gates) is a synchronous,
// side-effect-free function unit tests pin directly — the keydown handler itself
// needs a real DOM (`event.currentTarget.selectionStart`), which the node unit
// env lacks. The handler reads caret/selection LIVE off the DOM and hands the
// plain values in here; this module never touches the DOM.
//
// KNOWN LIMITATION (flagged, not solved — see PowerComposer's header): the
// `\n`-scan tests the LOGICAL line (text up to the nearest `\n`), not the VISUAL
// (wrapped) row. A long wrapped first/last logical line gates as first/last for
// its whole span, so recall fires one visual row "early". Non-destructive; a
// pixel-accurate fix needs mirror measurement (separable follow-up).

export type ComposerArrowRecall =
    | { readonly type: "recall_older" }
    | { readonly type: "recall_newer" }
    | { readonly type: "none" };

export type ComposerArrowInput = {
    readonly key: string;
    // slashOpen || mentionOpen. Menu-precedence: an open menu already owns
    // Arrow-Up/Down (its block preventDefault+returns FIRST in the handler, so
    // control never reaches recall while a menu is open). Threaded here too so
    // the decision is self-consistent and testable, not only guaranteed by
    // handler ordering — the same defense-in-depth the chat decider applies.
    readonly menuOpen: boolean;
    readonly selectionStart: number;
    readonly selectionEnd: number;
    readonly value: string;
    // True while the host is mid-recall (ChatSession.historyNav === at_history).
    // Arrow-Down only ever recalls when already recalling — you cannot "go
    // newer" without first going older (shell / address-bar feel).
    readonly historyNavActive: boolean;
    // Whether the caller supplied the corresponding recall callback (absent ⇒
    // that direction is plain native caret movement).
    readonly canRecallOlder: boolean;
    readonly canRecallNewer: boolean;
};

export const decideComposerArrow = (
    input: ComposerArrowInput,
): ComposerArrowRecall => {
    // Menu-precedence + no-selection gate apply to both directions. A selection
    // present ⇒ the arrow collapses it (native semantics), never recall.
    if (input.menuOpen) return { type: "none" };
    if (input.selectionStart !== input.selectionEnd) return { type: "none" };
    if (
        input.key === "ArrowUp" &&
        input.canRecallOlder &&
        // First logical line: no newline BEFORE the caret.
        input.value.slice(0, input.selectionStart).indexOf("\n") === -1
    )
        return { type: "recall_older" };
    if (
        input.key === "ArrowDown" &&
        input.historyNavActive &&
        input.canRecallNewer &&
        // Last logical line: no newline AFTER the caret (mirror of the up-gate).
        input.value.slice(input.selectionEnd).indexOf("\n") === -1
    )
        return { type: "recall_newer" };
    return { type: "none" };
};
